import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import MainLayout from '@/components/layout/MainLayout.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: 'Login - CRM', guest: true }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { title: 'Register - CRM', guest: true }
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { title: 'Dashboard - CRM', layout: MainLayout, requiresAuth: true }
  },
  {
    path: '/leads',
    name: 'leads',
    component: () => import('@/views/LeadsView.vue'),
    meta: { title: 'Leads - CRM', layout: MainLayout, requiresAuth: true }
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/views/ClientsView.vue'),
    meta: {
      title: 'Clients - CRM',
      layout: MainLayout,
      requiresAuth: true
    }
  },
  {
    path: '/projects',
    name: 'projects',
    component: () =>
      import('@/views/ProjectsView.vue'),
    meta: {
      title: 'Projects - CRM',
      layout: MainLayout,
      requiresAuth: true
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/ContactView.vue'),
    meta: { title: 'Leads - CRM', layout: MainLayout, requiresAuth: true }
  },
  {
    path: '/contacts/:id',
    name: 'contact.show',
    component: () => import('@/views/contacts/ContactDetail.vue'),
    meta: { title: 'Contact Details - CRM', layout: MainLayout, requiresAuth: true }
  },
  {
    path: '/verify-email',
    name: 'verify-email', 
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { title: 'Verify Email - CRM', requiresAuth: true }
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/onboarding/OnboardingView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/email/verify/:id/:hash',
    name: 'process-verification',
    component: () => import('@/views/auth/ProcessVerificationView.vue'),
    meta: { title: 'Verifying Email... - CRM' }
  },
  {
    path: '/reset-password',
    name: 'password.reset',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { guest: true }
  },
  {
  path: '/sales-pipeline',
  name: 'sales-pipeline',
  component: () => import('@/views/SalesPipelineView.vue'),
  meta: { title: 'Sales Pipeline - CRM', layout: MainLayout, requiresAuth: true }
},
  {
    path: '/settings',
    name: 'settings',
    component: () =>
      import('@/views/settings/SystemSettingsView.vue'),
    meta: {
      title: 'System Settings - CRM',
      layout: MainLayout,
      requiresAuth: true
    }
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

let isAppInitialized = false

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()

  // Execute backend validation on initial load if a token exists
  if (!isAppInitialized) {
    if (auth.token) {
      try {
        await auth.me()
      } catch (error) {
        // Interceptor in api.service.ts will handle the token clearance
      }
    }
    isAppInitialized = true
  }

  const requiresAuth = to.meta.requiresAuth
  const isGuestRoute = to.meta.guest
  const isVerified = !!auth.user?.email_verified_at

  if (requiresAuth && !auth.isAuthenticated) {
    return next({ name: 'login' })
  }

  if (auth.isAuthenticated && isGuestRoute) {
    return next({ name: 'dashboard' })
  }

  if (requiresAuth && !isVerified && to.name !== 'verify-email') {
    return next({ name: 'verify-email' })
  }

  if (auth.isAuthenticated && isVerified && to.name === 'verify-email') {
    return next({ name: 'dashboard' })
  }

  // Onboarding gate: verified user who hasn't completed onboarding
  if (
    requiresAuth &&
    auth.isAuthenticated &&
    isVerified &&
    !auth.isOnboardingCompleted &&
    to.name !== 'onboarding'
  ) {
    return next({ name: 'onboarding' })
  }

  // Prevent re-entry: completed user trying to go back to onboarding
  if (
    auth.isAuthenticated &&
    isVerified &&
    auth.isOnboardingCompleted &&
    to.name === 'onboarding'
  ) {
    return next({ name: 'dashboard' })
  }

  next()
})

export default router
